import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { API_URL } from '../../../../api-config';

const SITE = 'https://www.ladiora.com.tr';
const BRAND = 'Ladiora Boutique';
const NOINDEX_PATHS = ['/sepet', '/favoriler', '/hesabim', '/odeme', '/odeme-basarili', '/odeme-basarisiz'];

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

function upsertJsonLd(blocks: object[]) {
  document.head.querySelectorAll('script[data-ladiora-seo="1"]').forEach((n) => n.remove());
  blocks.forEach((block) => {
    const s = document.createElement('script');
    s.type = 'application/ld+json';
    s.setAttribute('data-ladiora-seo', '1');
    s.text = JSON.stringify(block);
    document.head.appendChild(s);
  });
}

export function SeoHead({
  title,
  description,
  image,
  jsonLd,
  noindex = false,
}: {
  title?: string;
  description?: string;
  image?: string;
  jsonLd?: object | object[] | null;
  noindex?: boolean;
}) {
  const location = useLocation();
  const [meta, setMeta] = useState<any>(null);

  useEffect(() => {
    const path = location.pathname || '/';
    let cancelled = false;
    fetch(`${API_URL}/api/seo/meta?path=${encodeURIComponent(path)}`)
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled && d?.basarili) setMeta(d.meta);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname]);

  useEffect(() => {
    const pageTitle = title || meta?.title || `${BRAND} | Şık Kadın Giyim`;
    const pageDesc = description || meta?.description || 'Kadın giyim, kimono, hoodie cape ve elbise — Ladiora Boutique.';
    const canonical = meta?.canonical || `${SITE}${location.pathname === '/' ? '' : location.pathname}`;
    const ogImage = image || meta?.ogImage || `${SITE}/as.svg`;
    const privatePath = NOINDEX_PATHS.some((p) => location.pathname === p || location.pathname.startsWith(`${p}/`));
    const indexable = !privatePath && !noindex && meta?.indexable !== false;
    const ld = jsonLd ?? meta?.jsonLd;
    const ldBlocks = ld ? (Array.isArray(ld) ? ld : [ld]) : [];

    document.title = pageTitle;
    upsertMeta('name', 'description', pageDesc);
    upsertMeta('name', 'robots', indexable ? 'index, follow' : 'noindex, nofollow');
    upsertLink('canonical', canonical);
    upsertMeta('property', 'og:title', pageTitle);
    upsertMeta('property', 'og:description', pageDesc);
    upsertMeta('property', 'og:url', canonical);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:site_name', BRAND);
    upsertMeta('property', 'og:locale', 'tr_TR');
    upsertMeta('property', 'og:image', ogImage);
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', pageTitle);
    upsertMeta('name', 'twitter:description', pageDesc);
    upsertMeta('name', 'twitter:image', ogImage);
    upsertJsonLd(ldBlocks);
  }, [title, description, image, jsonLd, noindex, meta, location.pathname]);

  return null;
}
